import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { isNativeAppUA } from 'dpl/shared/utils';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';

import StripeChargeModalPaymentSummaryLineItemV2 from './StripeChargeModalPaymentSummaryLineItemV2';

const IS_NATIVE_UA = isNativeAppUA();

function MobileTooltipDisplay({ description, tooltipDescription }) {
  return (
    <div className="MobileTooltipDisplay">
      <div className="mb6 bb b--lightest-gray">
        <h5 className="font-20 pb3 tc pt1">{description}</h5>
      </div>
      <p className="font-16 ph6">{tooltipDescription}</p>
    </div>
  );
}

function DesktopModalDisplay({ description, tooltipDescription }) {
  return (
    <div className="DesktopModalDisplay pv5">
      <div className="container">
        <div className="row pb3 mb6 bb b--lightest-gray">
          <h5 className="font-20 col tc">{description}</h5>
        </div>
        <div className="row pb6 ph8">
          <p className="font-16 tc">{tooltipDescription}</p>
        </div>
      </div>
    </div>
  );
}

export default function StripeChargeModalPaymentProcessingLineItem({
  amountInDollars,
  discountedPriceInDollars,
  paymentMethod,
  ...rest
}) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const showDiscount =
    paymentMethod && discountedPriceInDollars !== amountInDollars;

  function handleNativeTooltipClick() {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'OpenInBrowser',
        value: window.location.href
      })
    );
  }

  function handleIconClick() {
    setIsTooltipOpen(true);

    sendAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_MODAL_VIEWED_BY_BUYER, {
      view: ANALYTICS_VIEWS.PROCESSING_FEE_QUICK_VIEW,
      view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE
    });
  }

  const tooltipDescription = IS_NATIVE_UA ? (
    <span>
      100% of this fee goes to cover costs for payment processing. You can pay
      by bank transfer (ACH) for a full {discountedPriceInDollars} discount{' '}
      <button
        type="button"
        className="primary"
        onClick={handleNativeTooltipClick}
      >
        using our website
      </button>
      .
    </span>
  ) : (
    rest.tooltipDescription
  );

  return (
    <StripeChargeModalPaymentSummaryLineItemV2
      iconProps={{
        name: 'fetch-info'
      }}
      amountInDollars={amountInDollars}
      {...rest}
      discountedPriceInDollars={showDiscount ? discountedPriceInDollars : null}
      tooltipProps={{
        minWidth: '250px',
        title: (
          <MobileTooltipDisplay
            description={rest.description}
            tooltipDescription={tooltipDescription}
          />
        ),
        isVisible: isTooltipOpen,
        onClick: handleIconClick,
        onCloseTooltipClick: () => setIsTooltipOpen(false)
      }}
      modalProps={{
        content: (
          <DesktopModalDisplay
            description={rest.description}
            tooltipDescription={tooltipDescription}
          />
        )
      }}
    />
  );
}

MobileTooltipDisplay.propTypes = {
  description: PropTypes.string.isRequired,
  tooltipDescription: PropTypes.string.isRequired
};

DesktopModalDisplay.propTypes = {
  description: PropTypes.string.isRequired,
  tooltipDescription: PropTypes.string.isRequired
};

StripeChargeModalPaymentProcessingLineItem.propTypes = {
  amountInDollars: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  paymentMethod: PropTypes.string,
  longDescription: PropTypes.string,
  discountedPriceInDollars: PropTypes.string,
  tooltipDescription: PropTypes.string
};

StripeChargeModalPaymentProcessingLineItem.defaultProps = {
  longDescription: null,
  discountedPriceInDollars: null,
  tooltipDescription: null,
  paymentMethod: null
};

MobileTooltipDisplay.propTypes = {
  description: PropTypes.string.isRequired,
  tooltipDescription: PropTypes.string
};

MobileTooltipDisplay.defaultProps = {
  tooltipDescription: null
};
